import * as React from "react";
import { Check, ChevronRight, Dot } from "lucide-react";
import * as MenubarPrimitive from "@radix-ui/react-menubar";

import { cn } from "../lib/utils";

const MenubarMenu = MenubarPrimitive.Menu;

const MenubarGroup = MenubarPrimitive.Group;

const MenubarPortal = MenubarPrimitive.Portal;

const MenubarSub = MenubarPrimitive.Sub;

const MenubarRadioGroup = MenubarPrimitive.RadioGroup;

const Menubar = React.forwardRef<
  React.ElementRef<typeof MenubarPrimitive.Root>,
  React.ComponentPropsWithoutRef<typeof MenubarPrimitive.Root>
>(({ className, ...props }, ref) => (
  <MenubarPrimitive.Root
    ref={ref}
    className={cn(
      "tw-figr-flex tw-figr-h-9 tw-figr-items-center tw-figr-space-x-xxs tw-figr-rounded-sm tw-figr-border tw-figr-bg-base-white tw-figr-p-xxs tw-figr-shadow-sm",
      className
    )}
    {...props}
  />
));
Menubar.displayName = MenubarPrimitive.Root.displayName;

const MenubarTrigger = React.forwardRef<
  React.ElementRef<typeof MenubarPrimitive.Trigger>,
  React.ComponentPropsWithoutRef<typeof MenubarPrimitive.Trigger>
>(({ className, ...props }, ref) => (
  <MenubarPrimitive.Trigger
    ref={ref}
    className={cn(
      "tw-figr-flex tw-figr-cursor-default tw-figr-select-none tw-figr-items-center tw-figr-rounded-xs tw-figr-px-sm tw-figr-py-xxs tw-figr-text-desktop-caption-accent tw-figr-text-base-black tw-figr-outline-none focus:tw-figr-bg-neutral-100 data-[state=open]:tw-figr-bg-neutral-100 data-[state=open]:tw-figr-text-base-black",
      className
    )}
    {...props}
  />
));
MenubarTrigger.displayName = MenubarPrimitive.Trigger.displayName;

const MenubarSubTrigger = React.forwardRef<
  React.ElementRef<typeof MenubarPrimitive.SubTrigger>,
  React.ComponentPropsWithoutRef<typeof MenubarPrimitive.SubTrigger> & {
    inset?: boolean;
  }
>(({ className, inset, children, ...props }, ref) => (
  <MenubarPrimitive.SubTrigger
    ref={ref}
    className={cn(
      "tw-figr-flex tw-figr-cursor-default tw-figr-select-none tw-figr-items-center tw-figr-rounded-xs tw-figr-px-s tw-figr-py-xs tw-figr-text-desktop-caption-regular tw-figr-outline-none focus:tw-figr-bg-neutral-100 focus:tw-figr-text-base-black data-[state=open]:tw-figr-bg-neutral-100 data-[state=open]:tw-figr-text-base-black",
      inset && "tw-figr-pl-l",
      className
    )}
    {...props}
  >
    {children}
    <ChevronRight className="tw-figr-ml-auto tw-figr-h-4 tw-figr-w-4" />
  </MenubarPrimitive.SubTrigger>
));
MenubarSubTrigger.displayName = MenubarPrimitive.SubTrigger.displayName;

const MenubarSubContent = React.forwardRef<
  React.ElementRef<typeof MenubarPrimitive.SubContent>,
  React.ComponentPropsWithoutRef<typeof MenubarPrimitive.SubContent>
>(({ className, ...props }, ref) => (
  <MenubarPrimitive.SubContent
    ref={ref}
    className={cn(
      "tw-figr-z-50 tw-figr-min-w-[8rem] tw-figr-overflow-hidden tw-figr-rounded-sm tw-figr-border tw-figr-bg-base-white tw-figr-p-xxs tw-figr-text-base-black tw-figr-shadow-lg data-[state=open]:tw-figr-animate-in data-[state=closed]:tw-figr-animate-out data-[state=closed]:tw-figr-fade-out-0 data-[state=open]:tw-figr-fade-in-0 data-[state=closed]:tw-figr-zoom-out-95 data-[state=open]:tw-figr-zoom-in-95 data-[side=bottom]:tw-figr-slide-in-from-top-2 data-[side=left]:tw-figr-slide-in-from-right-2 data-[side=right]:tw-figr-slide-in-from-left-2 data-[side=top]:tw-figr-slide-in-from-bottom-2",
      className
    )}
    {...props}
  />
));
MenubarSubContent.displayName = MenubarPrimitive.SubContent.displayName;

const MenubarContent = React.forwardRef<
  React.ElementRef<typeof MenubarPrimitive.Content>,
  React.ComponentPropsWithoutRef<typeof MenubarPrimitive.Content>
>(
  (
    { className, align = "start", alignOffset = -4, sideOffset = 8, ...props },
    ref
  ) => (
    <MenubarPrimitive.Portal>
      <MenubarPrimitive.Content
        ref={ref}
        align={align}
        alignOffset={alignOffset}
        sideOffset={sideOffset}
        className={cn(
          "tw-figr-z-50 tw-figr-min-w-[12rem] tw-figr-overflow-hidden tw-figr-rounded-sm tw-figr-border tw-figr-bg-base-white tw-figr-p-xxs tw-figr-text-base-black tw-figr-shadow-md data-[state=open]:tw-figr-animate-in data-[state=closed]:tw-figr-fade-out-0 data-[state=open]:tw-figr-fade-in-0 data-[state=closed]:tw-figr-zoom-out-95 data-[state=open]:tw-figr-zoom-in-95 data-[side=bottom]:tw-figr-slide-in-from-top-2 data-[side=left]:tw-figr-slide-in-from-right-2 data-[side=right]:tw-figr-slide-in-from-left-2 data-[side=top]:tw-figr-slide-in-from-bottom-2",
          className
        )}
        {...props}
      />
    </MenubarPrimitive.Portal>
  )
);
MenubarContent.displayName = MenubarPrimitive.Content.displayName;

const MenubarItem = React.forwardRef<
  React.ElementRef<typeof MenubarPrimitive.Item>,
  React.ComponentPropsWithoutRef<typeof MenubarPrimitive.Item> & {
    inset?: boolean;
  }
>(({ className, inset, ...props }, ref) => (
  <MenubarPrimitive.Item
    ref={ref}
    className={cn(
      "tw-figr-relative tw-figr-flex tw-figr-cursor-default tw-figr-select-none tw-figr-items-center tw-figr-rounded-xs tw-figr-px-s tw-figr-py-xs tw-figr-text-desktop-caption-regular tw-figr-outline-none focus:tw-figr-bg-neutral-100 focus:tw-figr-text-base-black data-[disabled]:tw-figr-pointer-events-none data-[disabled]:tw-figr-opacity-50",
      inset && "tw-figr-pl-l",
      className
    )}
    {...props}
  />
));
MenubarItem.displayName = MenubarPrimitive.Item.displayName;

const MenubarCheckboxItem = React.forwardRef<
  React.ElementRef<typeof MenubarPrimitive.CheckboxItem>,
  React.ComponentPropsWithoutRef<typeof MenubarPrimitive.CheckboxItem>
>(({ className, children, checked, ...props }, ref) => (
  <MenubarPrimitive.CheckboxItem
    ref={ref}
    className={cn(
      "tw-figr-relative tw-figr-flex tw-figr-cursor-default tw-figr-select-none tw-figr-items-center tw-figr-rounded-xs tw-figr-py-xs tw-figr-pl-l tw-figr-pr-s tw-figr-text-desktop-caption-regular tw-figr-outline-none focus:tw-figr-bg-neutral-100 focus:tw-figr-text-base-black data-[disabled]:tw-figr-pointer-events-none data-[disabled]:tw-figr-opacity-50",
      className
    )}
    checked={checked}
    {...props}
  >
    <span className="tw-figr-absolute tw-figr-left-2 tw-figr-flex tw-figr-h-3.5 tw-figr-w-3.5 tw-figr-items-center tw-figr-justify-center">
      <MenubarPrimitive.ItemIndicator>
        <Check className="tw-figr-h-4 tw-figr-w-4" />
      </MenubarPrimitive.ItemIndicator>
    </span>
    {children}
  </MenubarPrimitive.CheckboxItem>
));
MenubarCheckboxItem.displayName = MenubarPrimitive.CheckboxItem.displayName;

const MenubarRadioItem = React.forwardRef<
  React.ElementRef<typeof MenubarPrimitive.RadioItem>,
  React.ComponentPropsWithoutRef<typeof MenubarPrimitive.RadioItem>
>(({ className, children, ...props }, ref) => (
  <MenubarPrimitive.RadioItem
    ref={ref}
    className={cn(
      "tw-figr-relative tw-figr-flex tw-figr-cursor-default tw-figr-select-none tw-figr-items-center tw-figr-rounded-xs tw-figr-py-xs tw-figr-pl-l tw-figr-pr-s tw-figr-text-desktop-caption-regular tw-figr-outline-none focus:tw-figr-bg-neutral-100 focus:tw-figr-text-base-black data-[disabled]:tw-figr-pointer-events-none data-[disabled]:tw-figr-opacity-50",
      className
    )}
    {...props}
  >
    <span className="tw-figr-absolute tw-figr-left-2 tw-figr-flex tw-figr-h-3.5 tw-figr-w-3.5 tw-figr-items-center tw-figr-justify-center">
      <MenubarPrimitive.ItemIndicator>
        <Dot className="tw-figr-h-4 tw-figr-w-4 tw-figr-fill-current" />
      </MenubarPrimitive.ItemIndicator>
    </span>
    {children}
  </MenubarPrimitive.RadioItem>
));
MenubarRadioItem.displayName = MenubarPrimitive.RadioItem.displayName;

const MenubarLabel = React.forwardRef<
  React.ElementRef<typeof MenubarPrimitive.Label>,
  React.ComponentPropsWithoutRef<typeof MenubarPrimitive.Label> & {
    inset?: boolean;
  }
>(({ className, inset, ...props }, ref) => (
  <MenubarPrimitive.Label
    ref={ref}
    className={cn(
      "tw-figr-px-s tw-figr-py-xs tw-figr-text-desktop-caption-accent",
      inset && "tw-figr-pl-l",
      className
    )}
    {...props}
  />
));
MenubarLabel.displayName = MenubarPrimitive.Label.displayName;

const MenubarSeparator = React.forwardRef<
  React.ElementRef<typeof MenubarPrimitive.Separator>,
  React.ComponentPropsWithoutRef<typeof MenubarPrimitive.Separator>
>(({ className, ...props }, ref) => (
  <MenubarPrimitive.Separator
    ref={ref}
    className={cn(
      "-tw-figr-mx-xxs tw-figr-my-xxs tw-figr-h-px tw-figr-bg-neutral-100",
      className
    )}
    {...props}
  />
));
MenubarSeparator.displayName = MenubarPrimitive.Separator.displayName;

const MenubarShortcut = ({
  className,
  ...props
}: React.HTMLAttributes<HTMLSpanElement>) => {
  return (
    <span
      className={cn(
        "tw-figr-ml-auto tw-figr-text-xs tw-figr-tracking-widest tw-figr-text-neutral-500",
        className
      )}
      {...props}
    />
  );
};
MenubarShortcut.displayname = "MenubarShortcut";

export {
  Menubar,
  MenubarMenu,
  MenubarTrigger,
  MenubarContent,
  MenubarItem,
  MenubarSeparator,
  MenubarLabel,
  MenubarCheckboxItem,
  MenubarRadioGroup,
  MenubarRadioItem,
  MenubarPortal,
  MenubarSubContent,
  MenubarSubTrigger,
  MenubarGroup,
  MenubarSub,
  MenubarShortcut
};
